/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import { css } from '@emotion/react';

import Text from '@mui/material/Typography';

import SourceIcon from '../SourceIcon';
import MeetingStatus from '../../meetings/MeetingStatusNew';
import { getSpeakerColor } from '../../../utils/speaker';

function TranscriptChunk({ chunk }) {
  const singleSpeaker = useMemo(() => {
    const speaker = chunk?.[0]?.speaker;
    if (!speaker) return '';

    if (speaker.includes(',')) {
      return speaker.split(',')[0];
    }

    return speaker;
  }, [chunk]);

  const color = useMemo(() => getSpeakerColor(singleSpeaker), [singleSpeaker]);

  return (
    <div
      css={css`
        display: flex;
        padding: 4px;
        gap: 8px;
      `}
    >
      <div
        css={css`
          background-color: ${color};
          min-width: 8px;
          min-height: 8px;
          max-width: 8px;
          max-height: 8px;
          border-radius: 50%;
          margin-top: 6px;
        `}
      />
      <Text variant="body2" fontSize="0.875rem">
        {chunk.map((c) => c.text).join(' ')}
      </Text>
    </div>
  );
}

function TranscriptSnapshot({ texts, maxWidth }) {
  const chunks = useMemo(() => {
    if (!texts?.length) return [];
    const result = [];

    let currentChunk = [];

    let previousItem = null;

    for (let i = 0; i < texts.length; i++) {
      const item = texts[i];

      if (previousItem && previousItem.speaker !== item.speaker) {
        result.push(currentChunk);
        currentChunk = [];
      } else if (previousItem && item.start - previousItem.end > 1) {
        result.push(currentChunk);
        currentChunk = [];
      } else if (item.force_split) {
        result.push(currentChunk);
        currentChunk = [];
      }

      currentChunk.push(item);

      previousItem = item;
    }

    if (currentChunk.length > 0) {
      result.push(currentChunk);
    }

    return result;
  }, [texts]);

  return (
    <div
      css={css`
        width: 106px;
        display: flex;
        height: 100%;
        flex-direction: column;
        font-size: 0.5rem;
        width: 100%;
        max-width: ${maxWidth}px;
        background: #fff;
        max-height: 152px;
        overflow: hidden;
        user-select: none;
        border-radius: 4px;
        padding: 0 4px;
        box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2),
          0px 4px 5px 0px rgba(0, 0, 0, 0.14),
          0px 1px 10px 0px rgba(0, 0, 0, 0.12);
      `}
    >
      {chunks.map((chunk, i) => (
        <TranscriptChunk key={i} chunk={chunk} />
      ))}
    </div>
  );
}

export default function AssetSnapshot({
  id,
  name,
  image,
  mimetype,
  mediaType,
  source,
  texts,
  status,
  type,
  maxWidth = 232,
  isInViewport = false,
}) {
  if (type === 'transcript' && status !== 'ready') {
    return (
      <div
        css={css`
          width: 106px;
          display: flex;
          height: 100%;
          align-items: center;
          gap: 0.5rem;
          flex-direction: column;
          justify-content: center;
          font-size: 0.5rem;
          width: 100%;
          max-width: ${maxWidth}px;
          background: var(--grey-100);
          max-height: 152px;
          overflow: hidden;
          user-select: none;
          border-radius: 4px;
          padding: 0 4px;
        `}
      >
        <MeetingStatus status={status} />
      </div>
    );
  }
  if (image && isInViewport) {
    return (
      <img
        src={image}
        alt={name}
        css={css`
          width: auto;
          height: 100%;
          max-width: ${maxWidth}px;
          border-radius: 4px;
          object-fit: cover;
        `}
      />
    );
  }

  if (texts?.length && mediaType !== 'video') {
    return <TranscriptSnapshot texts={texts} maxWidth={maxWidth} />;
  }

  return (
    <div
      css={css`
        width: 106px;
        border: 1px solid #0000003b;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        border-radius: 4px;
      `}
    >
      <SourceIcon
        source={source}
        mimetype={mimetype}
        size="large"
        width="56px"
        height="56px"
      />
    </div>
  );
}
